import React, { createContext, useContext, useEffect, useState } from 'react';

const CACHE_KEY = 'store-settings-v2';

const DEFAULT = {
  payment_methods: [],
  shipping: { cost: 200 },
  whatsapp_number: '',
  topBanner:  { enabled: false, image_url: '', link: '', height: 36 },
  popularCategories: [],
  categoryStrip: [],
  homepageCategories: [{ type: 'new_arrivals' }],
  social: { facebook: '', instagram: '', instagram_handle: '' },
  logo:       { text: 'Store', dot: '', tagline: '', color: '#17140F', accent_color: '#234D3B' },
  ticker:     { enabled: true, messages: ['Free Shipping on orders over Rs 2000', 'New Arrivals Every Week', 'Secure & Encrypted Checkout', 'Free 30-Day Returns'], bg_color: '#fefce8', text_color: '#92400e' },
  hero:       { tag: '✦ New arrivals every week', title: 'Shop the <em>future</em> of online retail', subtitle: 'Discover curated collections across electronics, fashion, and home, all in one place, delivered fast.', btn1_text: 'Shop Now', btn1_link: '/products', btn2_text: 'Browse Categories', btn2_link: '/products', stat1: { value: '50k+', label: 'Products' }, stat2: { value: '200k+', label: 'Customers' }, stat3: { value: '4.9★', label: 'Avg Rating' } },
  categories: { enabled: true, label: 'Collections', title: 'Shop by Category', subtitle: 'Explore our handpicked selections, with new categories added every month' },
  featured:   { enabled: true, label: 'Featured',    title: 'Trending This Week', subtitle: 'Our most popular products, loved by thousands of customers' },
  staffFavorites: { enabled: true, title: 'Staff Favorites', subtitle: 'Most liked by us', products: [] },
  newArrivals:    { enabled: true, title: 'New Arrivals', subtitle: 'Discover something New', products: [] },
  carouselCats: [],
  featuredCats: [],
  navLinks: [
    { label: 'Home',         to: '/'              },
    { label: 'New Arrivals', to: '/new-arrivals' },
    { label: 'Deals',        to: '/deals'         },
    { label: 'Most Selling', to: '/most-selling'  },
    { label: 'About Us',     to: '/about'         },
    { label: 'Contact',      to: '/contact'       },
  ],
};

const Ctx = createContext({ settings: DEFAULT, editMode: false, ready: true });

export function StoreSettingsProvider({ children }) {
  const [settings, setSettings] = useState(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      return cached ? JSON.parse(cached) : DEFAULT;
    } catch { return DEFAULT; }
  });
  // True once we've either loaded a cached copy or the live fetch has resolved —
  // lets nav-dependent UI wait instead of flashing hard-coded defaults first.
  const [ready, setReady] = useState(() => {
    try { return !!localStorage.getItem(CACHE_KEY); } catch { return false; }
  });
  // Auto-enter edit mode when page is loaded inside admin iframe
  const [editMode, setEditMode] = useState(() => typeof window !== 'undefined' && window.parent !== window);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1'}/store/settings`)
      .then(r => r.json())
      .then(d => {
        if (d.settings) {
          setSettings(d.settings);
          try { localStorage.setItem(CACHE_KEY, JSON.stringify(d.settings)); } catch {}
        }
      })
      .catch(() => {})
      .finally(() => setReady(true));

    const handle = (e) => {
      if (e.data?.type === 'PREVIEW_UPDATE')  setSettings(e.data.settings);
      if (e.data?.type === 'ENTER_EDIT_MODE') setEditMode(true);
      if (e.data?.type === 'EXIT_EDIT_MODE')  setEditMode(false);
    };
    window.addEventListener('message', handle);
    return () => window.removeEventListener('message', handle);
  }, []);

  // In edit mode (inside the iframe), block all link navigation so clicks
  // stay on the page and still bubble up to section click handlers.
  useEffect(() => {
    if (!editMode) return;
    const blockNav = (e) => {
      const a = e.target.closest('a, button[type="submit"]');
      if (a) e.preventDefault();
    };
    document.addEventListener('click', blockNav, true);
    return () => document.removeEventListener('click', blockNav, true);
  }, [editMode]);

  return (
    <Ctx.Provider value={{ settings, editMode, ready }}>
      {children}
    </Ctx.Provider>
  );
}

export const useStoreSettings = () => useContext(Ctx);
